/**
 * API Settings Page
 * Configure external API integrations (Open Exchange Rates)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  Switch,
  FormControlLabel,
  CircularProgress,
  Chip,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  PlayArrow as PlayArrowIcon,
  CurrencyExchange as CurrencyExchangeIcon,
} from '@mui/icons-material';
import { apiGet, apiPut, apiPost } from '../../utils/apiClient';
import logger from '../../utils/logger';

function ApiSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [message, setMessage] = useState(null);
  const [settings, setSettings] = useState({
    openexchangerates_app_id: '',
    openexchangerates_enabled: 'false',
    openexchangerates_last_fetch: '',
    fx_fetch_frequency: 'monthly',
    fx_effective_date_strategy: 'first_of_month',
    fx_pivot_currency: 'USD',
  });
  const [showAppId, setShowAppId] = useState(false);
  const [appIdHasValue, setAppIdHasValue] = useState(false);
  const [latestRates, setLatestRates] = useState([]);
  const [currencyPairs, setCurrencyPairs] = useState([]);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);

      // Load API settings
      const response = await apiGet('/exchange-rates/settings');
      if (response.success && response.settings) {
        const settingsObj = {
          openexchangerates_app_id: response.settings.openexchangerates_app_id?.value || '',
          openexchangerates_enabled: response.settings.openexchangerates_enabled?.value || 'false',
          openexchangerates_last_fetch: response.settings.openexchangerates_last_fetch?.value || '',
          fx_fetch_frequency: response.settings.fx_fetch_frequency?.value || 'monthly',
          fx_effective_date_strategy: response.settings.fx_effective_date_strategy?.value || 'first_of_month',
          fx_pivot_currency: response.settings.fx_pivot_currency?.value || 'USD',
        };
        setSettings(settingsObj);
        setAppIdHasValue(response.settings.openexchangerates_app_id?.hasValue || false);
      }

      // Load currency pairs
      const pairsResponse = await apiGet('/exchange-rates/pairs');
      if (pairsResponse.success) {
        setCurrencyPairs(pairsResponse.pairs || []);
      }

      // Load latest rates — use 'last_fetched' mode so the admin sees what the
      // most recent fetch produced, regardless of whether the strategy
      // backdated the effective_date.
      const ratesResponse = await apiGet('/exchange-rates/latest?mode=last_fetched');
      if (ratesResponse.success) {
        setLatestRates(ratesResponse.rates || []);
      }
    } catch (error) {
      logger.error('Error loading API settings', { message: error.message, status: error.response?.status });
      setMessage({ type: 'error', text: 'Failed to load API settings' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSaveAppId = async () => {
    try {
      setSaving(true);
      setMessage(null);

      await apiPut('/exchange-rates/settings/openexchangerates_app_id', {
        value: settings.openexchangerates_app_id,
      });

      setMessage({ type: 'success', text: 'App ID saved successfully' });
      setAppIdHasValue(!!settings.openexchangerates_app_id);
      setShowAppId(false);
    } catch (error) {
      logger.error('Error saving App ID', { message: error.message, status: error.response?.status });
      setMessage({ type: 'error', text: error.message || 'Failed to save App ID' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async () => {
    try {
      setSaving(true);
      setMessage(null);

      const newValue = settings.openexchangerates_enabled === 'true' ? 'false' : 'true';
      await apiPut('/exchange-rates/settings/openexchangerates_enabled', { value: newValue });

      setSettings(prev => ({ ...prev, openexchangerates_enabled: newValue }));
      setMessage({ type: 'success', text: `Exchange rates integration ${newValue === 'true' ? 'enabled' : 'disabled'}` });
    } catch (error) {
      logger.error('Error toggling enabled state', { message: error.message, status: error.response?.status });
      setMessage({ type: 'error', text: error.message || 'Failed to update setting' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setMessage(null);

      const response = await apiPost('/exchange-rates/test-connection');

      if (response.success) {
        setMessage({ type: 'success', text: response.message });
      } else {
        setMessage({ type: 'error', text: response.message || 'Connection test failed' });
      }
    } catch (error) {
      logger.error('Error testing connection', { message: error.message, status: error.response?.status });
      setMessage({ type: 'error', text: error.message || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  // Mirrors capitalmanagement-api/services/exchangeRatesService.js resolveEffectiveDate.
  // Returns the date the *next* fetch will stamp on rate rows for the given strategy.
  const previewNextEffectiveDate = (strategy, now = new Date()) => {
    if (strategy === 'today') return new Date(now);
    if (strategy === 'last_business_day_of_month') {
      // Day 0 of current month = last calendar day of previous month.
      const d = new Date(now.getFullYear(), now.getMonth(), 0);
      while (d.getDay() === 0 || d.getDay() === 6) {
        d.setDate(d.getDate() - 1);
      }
      return d;
    }
    // first_of_month (default)
    return new Date(now.getFullYear(), now.getMonth(), 1);
  };

  const handleSaveFxSetting = async (key, value) => {
    try {
      setSaving(true);
      setMessage(null);

      await apiPut(`/exchange-rates/settings/${key}`, { value });
      setSettings(prev => ({ ...prev, [key]: value }));

      if (key === 'fx_effective_date_strategy') {
        setMessage({
          type: 'info',
          text: 'Strategy saved. Existing rates keep their original effective date. Click "Fetch Rates Now" to apply the new strategy.',
        });
      } else {
        setMessage({ type: 'success', text: 'Setting saved' });
      }
    } catch (error) {
      logger.error('Error saving FX setting', { message: error.message, status: error.response?.status, event: key });
      setMessage({ type: 'error', text: error.message || 'Failed to save setting' });
    } finally {
      setSaving(false);
    }
  };

  const handleFetchRates = async () => {
    try {
      setFetching(true);
      setMessage(null);

      const response = await apiPost('/exchange-rates/fetch');

      if (response.success) {
        const totals = (response.results || []).reduce((acc, r) => {
          acc.inserted += (r.inserted || []).length;
          acc.updated  += (r.updated  || []).length;
          return acc;
        }, { inserted: 0, updated: 0 });
        const bases = (response.bases || []).join(', ');
        setMessage({
          type: 'success',
          text: `Rates fetched successfully${bases ? ` (bases: ${bases})` : ''}. ${totals.inserted} inserted, ${totals.updated} updated.`,
        });
        // Reload to show new data
        await loadSettings();
      } else {
        setMessage({ type: 'error', text: response.error || 'Failed to fetch rates' });
      }
    } catch (error) {
      logger.error('Error fetching rates', { message: error.message, status: error.response?.status });
      setMessage({ type: 'error', text: error.message || 'Failed to fetch rates' });
    } finally {
      setFetching(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            API Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure external API integrations for the application
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadSettings}
            disabled={saving || testing || fetching}
          >
            Refresh
          </Button>
        </Box>
      </Box>
      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}
      {/* Open Exchange Rates Configuration */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <CurrencyExchangeIcon
            color={settings.openexchangerates_enabled === 'true' ? 'primary' : 'disabled'}
            sx={{ fontSize: 40 }}
          />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6">
              Open Exchange Rates API
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Provides live and historical foreign exchange rates
            </Typography>
          </Box>
          <Chip
            label={settings.openexchangerates_enabled === 'true' ? 'ENABLED' : 'DISABLED'}
            color={settings.openexchangerates_enabled === 'true' ? 'success' : 'default'}
          />
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          {/* Enable/Disable Toggle */}
          <Grid size={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.openexchangerates_enabled === 'true'}
                  onChange={handleToggleEnabled}
                  disabled={saving || !appIdHasValue}
                />
              }
              label="Enable automatic rate fetching"
            />
            {!appIdHasValue && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                (Configure App ID first)
              </Typography>
            )}
          </Grid>

          {/* App ID Configuration */}
          <Grid
            size={{
              xs: 12,
              md: 8
            }}>
            <TextField
              fullWidth
              label="App ID"
              type={showAppId ? 'text' : 'password'}
              value={settings.openexchangerates_app_id}
              onChange={(e) => setSettings(prev => ({ ...prev, openexchangerates_app_id: e.target.value }))}
              placeholder={appIdHasValue ? '********' : 'Enter your App ID'}
              helperText="Your Open Exchange Rates API App ID. Get one at openexchangerates.org"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowAppId(!showAppId)}
                      edge="end"
                    >
                      {showAppId ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
            size={{
              xs: 12,
              md: 4
            }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveAppId}
              disabled={saving || !settings.openexchangerates_app_id}
            >
              {saving ? 'Saving...' : 'Save App ID'}
            </Button>
          </Grid>

          {/* Last Fetch Info */}
          {settings.openexchangerates_last_fetch && (
            <Grid size={12}>
              <Typography variant="body2" color="text.secondary">
                <strong>Last successful fetch:</strong>{' '}
                {new Date(settings.openexchangerates_last_fetch).toLocaleString()}
              </Typography>
            </Grid>
          )}

          {/* Action Buttons */}
          <Grid size={12}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={testing ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                onClick={handleTestConnection}
                disabled={testing || !appIdHasValue}
              >
                {testing ? 'Testing...' : 'Test Connection'}
              </Button>
              <Button
                variant="outlined"
                startIcon={fetching ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={handleFetchRates}
                disabled={fetching || settings.openexchangerates_enabled !== 'true'}
              >
                {fetching ? 'Fetching...' : 'Fetch Rates Now'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      {/* FX Behaviour Settings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          FX Behaviour
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Controls how often rates are fetched, the effective date written to each row,
          and the fallback pivot currency used for triangulation when a tenant has no base set.
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel id="fx-fetch-frequency-label">Fetch Frequency</InputLabel>
              <Select
                labelId="fx-fetch-frequency-label"
                label="Fetch Frequency"
                value={settings.fx_fetch_frequency}
                onChange={(e) => handleSaveFxSetting('fx_fetch_frequency', e.target.value)}
                disabled={saving}
              >
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly (Mondays)</MenuItem>
                <MenuItem value="monthly">Monthly (1st of month)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel id="fx-effective-date-label">Effective Date</InputLabel>
              <Select
                labelId="fx-effective-date-label"
                label="Effective Date"
                value={settings.fx_effective_date_strategy}
                onChange={(e) => handleSaveFxSetting('fx_effective_date_strategy', e.target.value)}
                disabled={saving}
              >
                <MenuItem value="first_of_month">First of month</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="last_business_day_of_month">Last business day of previous month</MenuItem>
              </Select>
              <FormHelperText>
                Next fetch will write effective date:{' '}
                <strong>
                  {previewNextEffectiveDate(settings.fx_effective_date_strategy).toLocaleDateString()}
                </strong>
              </FormHelperText>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Fallback Pivot Currency"
              value={settings.fx_pivot_currency}
              onChange={(e) => setSettings(prev => ({ ...prev, fx_pivot_currency: e.target.value.toUpperCase() }))}
              onBlur={(e) => {
                const v = e.target.value.toUpperCase();
                if (/^[A-Z]{3}$/.test(v)) handleSaveFxSetting('fx_pivot_currency', v);
              }}
              inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
              helperText="3-letter ISO code (e.g. USD, ZAR). Used when a tenant has no defaultCurrency."
              disabled={saving}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Configured Currency Pairs */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Configured Currency Pairs
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Rates will only be fetched for currency pairs configured in Reference Data.
          To add more pairs, go to Reference Data &gt; FX Rates.
        </Typography>

        {currencyPairs.length > 0 ? (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {currencyPairs.map((pair) => (
              <Chip key={pair} label={pair} variant="outlined" />
            ))}
          </Box>
        ) : (
          <Alert severity="info">
            No currency pairs configured. Add currency pairs in Reference Data to enable rate fetching.
          </Alert>
        )}
      </Paper>
      {/* Current Rates */}
      {latestRates.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Current Exchange Rates
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Most recent rates for each configured currency pair
          </Typography>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Currency Pair</TableCell>
                  <TableCell align="right">Rate</TableCell>
                  <TableCell>Effective Date</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {latestRates.map((rate) => (
                  <TableRow key={rate.id}>
                    <TableCell>
                      <strong>{rate.currency_pair}</strong>
                    </TableCell>
                    <TableCell align="right">
                      {parseFloat(rate.rate).toFixed(6)}
                    </TableCell>
                    <TableCell>
                      {new Date(rate.effective_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={rate.source || 'Manual'}
                        size="small"
                        color={rate.source === 'OpenExchangeRates' ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {rate.updated_at
                        ? new Date(rate.updated_at).toLocaleString()
                        : new Date(rate.created_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}

export default ApiSettingsPage;
